import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

let stripePromise = null;

export default function Checkout() {
  const { items } = useCart();
  const { state } = useLocation();
  const [pk, setPk] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/payments/config")
      .then((r) => setPk(r.data.publishable_key))
      .catch(() => setError(true));
  }, []);

  const fetchClientSecret = useCallback(async () => {
    const res = await api.post("/payments/checkout", {
      items: items.map((i) => ({ id: i.id, quantity: i.quantity })),
      origin_url: window.location.origin,
      email: state?.email || undefined,
    });
    return res.data.client_secret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0) return <Navigate to="/cart" replace />;

  if (pk && !stripePromise) stripePromise = loadStripe(pk);

  return (
    <div data-testid="checkout-page" className="pt-28 pb-16 min-h-screen bg-[#F8F5F0]">
      <div className="max-w-3xl mx-auto px-6">
        <Link
          to="/cart"
          data-testid="checkout-back-to-cart"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A3F35] hover:text-[#C6A769] transition-colors duration-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Cart
        </Link>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#4A3F35] mb-8">Checkout</h1>

        {error && (
          <div data-testid="checkout-error" className="text-center py-20">
            <p className="text-[#8A7D71]">We couldn't load checkout. Please try again.</p>
            <Link to="/cart" className="mt-6 inline-block text-[#C6A769] underline underline-offset-2">
              Return to cart
            </Link>
          </div>
        )}

        {!error && !pk && (
          <div data-testid="checkout-loading" className="text-center py-20">
            <Loader2 className="w-10 h-10 text-[#C6A769] mx-auto animate-spin" strokeWidth={1.2} />
            <p className="text-[#8A7D71] mt-4">Preparing your secure checkout…</p>
          </div>
        )}

        {!error && pk && (
          <div
            data-testid="embedded-checkout-container"
            className="bg-[#FAF7F2] border border-[#C6A769]/30 rounded-sm p-4 sm:p-6"
          >
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        <p className="text-xs text-[#8A7D71] text-center mt-6">
          All sales are final — digital products are delivered instantly.{" "}
          <Link to="/terms" className="text-[#C6A769] underline underline-offset-2">Terms of Use</Link>
        </p>
      </div>
    </div>
  );
}
